import { Validators, ValidatorFn } from '@angular/forms';
import { FieldOption, RecordSection } from '@k12/app-components';
import { nameValidator, ssnValidator, wholeDollarsValidator } from '@k12/validators';
import { ApplicationStatus } from '@k12/models';

const guardianPidValidator: ValidatorFn = (control) => {
  const value = control.value;
  if (value == null || value === '' || typeof value === 'object') return null;
  return { message: 'Guardian PID not found.' };
};

const validateSsn = ssnValidator();

/** Shown wherever a residency value is null: the household has not been evaluated yet. */
const NOT_EVALUATED = 'Not Evaluated';

/** Rejects negative currency entries. Blank is left alone — these amounts are optional. */
const nonNegativeAmount: ValidatorFn = Validators.min(0);

/** Costs (standard, individual and term) are whole-dollar amounts. */
const wholeDollarAmount: ValidatorFn = wholeDollarsValidator();

/**
 * Requires a full, well-formed SSN. Blank is left to the edit component, which makes the field
 * required for the admins who can actually see it.
 * @see StudentInformationEditComponent.registerSsnHandler
 */
const studentSsnValidator: ValidatorFn = (control) => {
  const value = control.value;
  if (value == null || value === '') return null;
  return validateSsn(control) ? { message: 'Enter a valid Social Security Number (XXX-XX-XXXX).' } : null;
};

const IS_AWARDED = 'isAwarded';

export const STUDENT_INFORMATION_SECTIONS: RecordSection[] = [
  {
    title: 'Student Information',
    fields: [
      // A parent can't rename a student once they're on the account, so the name is admin-only here.
      {
        key: 'firstName',
        label: 'First Name',
        type: 'text',
        validators: [Validators.required, Validators.maxLength(250), nameValidator()],
        editOnly: true,
        editableBy: ['admin']
      },
      { key: 'middleName', label: 'Middle Name', type: 'text', editOnly: true, editableBy: ['admin'] },
      {
        key: 'lastName',
        label: 'Last Name',
        type: 'text',
        validators: [Validators.required, Validators.maxLength(250), nameValidator()],
        editOnly: true,
        editableBy: ['admin']
      },
      {
        key: 'suffix',
        label: 'Suffix',
        type: 'text',
        validators: [Validators.maxLength(250)],
        editOnly: true,
        editableBy: ['admin']
      },
      { key: 'dob', label: 'Date of Birth', type: 'date', validators: [Validators.required], editableBy: ['admin'] },
      { key: 'ssn', label: 'Social Security', type: 'text', format: 'ssn', placeholder: 'Enter all 9 digits', editableBy: ['admin'], validators: [studentSsnValidator] },
      { key: 'guardianPid', label: 'Parent PID', type: 'autocomplete', optionsKey: 'households', asyncSearch: true, readonly: true, validators: [guardianPidValidator] },
      { key: 'ethnicity', label: 'Ethnicity', type: 'select', optionsKey: 'ethnicity', editableBy: ['admin','household'] },
      { key: 'race', label: 'Race', type: 'select', optionsKey: 'race', editableBy: ['admin','household'] },
      { key: 'sex', label: 'Sex', type: 'select', optionsKey: 'sex', editableBy: ['admin','household'] },
      {
        // Tri-state: an admin must be able to clear the override back to "Not Evaluated". mat-select
        // cannot render a null option value, so the option carries the same 'not-evaluated' sentinel
        // the household screen uses and onSave maps it back to null.
        key: 'ncResidency',
        label: 'NC Resident',
        type: 'select',
        optionsKey: 'ncResidency',
        nullLabel: NOT_EVALUATED,
        editableBy: ['admin', 'household'],
        readonlyWhen: {
            field: IS_AWARDED,
            is: true,
            message: 'NC Resident cannot be changed after student is awarded.'
        }
      },
      {
        // Household-level fact, not per-student: the API reads it off K12.Household, so editing it
        // here would silently change the value for every sibling in the household. It is set on the
        // household record instead, and onSave omits it from the payload so a save can never write it.
        key: 'ncResidencyVerified',
        label: 'Residency Verified',
        type: 'boolean',
        readonly: true,
        nullLabel: NOT_EVALUATED
      }
    ]
  },
  {
    title: 'Application Details',
    fields: [
      { key: 'osApplicationStatus', label: 'Opportunity Status', type: 'text', readonly: true },
      {
        key: 'osIneligibilityReason',
        label: 'Opportunity Scholarship Ineligibility Reason',
        type: 'text',
        showWhen: { field: 'osApplicationStatus', is: ApplicationStatus.Ineligible },
        readonly: true
      },
      { key: 'osAwardStatus', label: 'Opportunity Award Tier', type: 'text', readonly: true },
      {
        key: 'tier',
        label: 'Opportunity Scholarship Tier',
        type: 'text',
        showWhen: { field: 'osApplicationStatus', is: ApplicationStatus.Eligible },
        readonly: true
      },
      { key: 'esaApplicationStatus', label: 'ESA+ Application Status', type: 'text', readonly: true },
      {
        key: 'esaIneligibilityReason',
        label: 'ESA+ Ineligibility Reason',
        type: 'text',
        showWhen: { field: 'esaApplicationStatus', is: ApplicationStatus.Ineligible },
        readonly: true
      },
      { key: 'esaAwardStatus', label: 'ESA+ Status', type: 'text', readonly: true },
      {
        key: 'esaFundingLevel',
        label: 'ESA+ Funding Level',
        type: 'text',
        showWhen: { field: 'esaApplicationStatus', is: ApplicationStatus.Eligible },
        readonly: true
      },
      { key: 'hasHighSchoolDiploma', label: 'High School Diploma', type: 'boolean', editableBy: ['admin', 'household'],
        readonlyWhen: {
            field: IS_AWARDED,
            is: true,
            message: 'Has High School Diploma cannot be changed after student is awarded.'
        }
       },
      { key: 'enrolledInCollege', label: 'Enrolled in College', type: 'boolean', editableBy: ['admin', 'household'] ,
        readonlyWhen: {
            field: IS_AWARDED,
            is: true,
            message: 'Enrolled in College cannot be changed after student is awarded.'
        }
      }
    ]
  },
  {
    title: 'School Details',
    fields: [
      { key: 'schoolId',
        label: 'School Name',
        type: 'search-select', optionsKey: 'schools',
        editableBy: ['admin', 'household'],
        readonlyWhen: {
            field: 'certificationDate',
            is: 'not-null',
            message: 'School cannot be changed after student is certified.'
        }
      },
      { key: 'attendanceTypeId', label: 'Enrollment Status', type: 'select', optionsKey: 'enrollmentTypes', editableBy: ['admin'] },
      { key: 'gradeId',
        label: 'Grade Level',
        type: 'select',
        optionsKey: 'gradeLevels',
        editableBy: ['admin', 'household'],
        readonlyWhen: {
            field: 'certificationDate',
            is: 'not-null',
            message: 'Grade Level cannot be changed after student is certified.'
        }
      },
      { key: 'certificationDate', label: 'Certification Date', type: 'date', editableBy: ['admin'] },
      {
        // Derived from the school's Certification & Endorsement step, so it is never editable here.
        key: 'endorsedBySchoolFall',
        label: 'Fall Term Endorsed by School',
        type: 'boolean',
        readonly: true
      },
      {
        key: 'endorsedBySchoolSpring',
        label: 'Spring Term Endorsed by School',
        type: 'boolean',
        readonly: true
      },
      {
        key: 'homeschoolIdNumber',
        label: 'Homeschool ID Number',
        type: 'text',
        editableBy: ['household'],
        showWhen: { field: 'schoolTypeId', is: [3, '3'] }
      },
      {
        key: 'homeschoolName',
        label: 'Homeschool Name',
        type: 'text',
        editableBy: ['household'],
        showWhen: { field: 'schoolTypeId', is: [3, '3'] }
      },
      {
        key: 'homeschoolAdministratorName',
        label: 'Homeschool Administrator Name',
        type: 'text',
        editableBy: ['household'],
        showWhen: { field: 'schoolTypeId', is: [3, '3'] }
      },
      {
        key: 'homeschoolCounty',
        label: 'Homeschool County',
        type: 'text',
        editableBy: ['household'],
        showWhen: { field: 'schoolTypeId', is: [3, '3'] }
      },
    ]
  },
  {
    title: 'School Cost',
    // Costs and funds are amounts owed or awarded, never credits, so none of them may go below zero.
    fields: [
      { key: 'academicYearStandardCostTotal', label: 'Standard Cost', type: 'currency', validators: [nonNegativeAmount, wholeDollarAmount], editableBy: ['admin'] },
      { key: 'academicYearIndividualCostTotal', label: 'Individual Cost', type: 'currency', validators: [nonNegativeAmount, wholeDollarAmount], editableBy: ['admin'] },
      { key: 'fallCost', label: 'Fall Cost', type: 'currency', validators: [nonNegativeAmount, wholeDollarAmount], editableBy: ['admin'] },
      { key: 'springCost', label: 'Spring Cost', type: 'currency', validators: [nonNegativeAmount, wholeDollarAmount], editableBy: ['admin'] },
      { key: 'cwFallFunds', label: 'Wallet Fall Fund', type: 'currency', validators: [nonNegativeAmount], readonly: true },
      { key: 'cwSpringFunds', label: 'Wallet Spring Fund', type: 'currency', validators: [nonNegativeAmount], readonly: true },
      { key: 'cwFundsPaid', label: 'Wallet Fund Paid', type: 'currency', validators: [nonNegativeAmount], readonly: true }
    ]
  }
];

export const STUDENT_INFORMATION_OPTIONS: Record<string, FieldOption[]> = {
  yesNo: [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ],
  ethnicity: [
    { label: 'Hispanic or Latino', value: 'Hispanic or Latino' },
    { label: 'Not Hispanic or Latino', value: 'Not Hispanic or Latino' }
  ],
  race: [
    { label: 'American Indian or Alaska Native', value: 'American Indian or Alaska Native' },
    { label: 'Asian', value: 'Asian' },
    { label: 'Black or African American', value: 'Black or African American' },
    { label: 'Native Hawaiian or Other Pacific Islander', value: 'Native Hawaiian or Other Pacific Islander' },
    { label: 'White', value: 'White' },
    { label: 'Prefer not to answer', value: 'Prefer not to answer' }
  ],
  sex: [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' }
  ],
  ncResidency: [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
    { label: NOT_EVALUATED, value: 'not-evaluated' }
  ],
  enrollmentTypes: [
    { label: 'Not Yet Certified', value: 0 },
    { label: 'Full Time', value: 1 },
    { label: 'Part Time', value: 2 },
    { label: 'Not Attending', value: 3 }
  ],
  schools: [],
  schoolTypes: [],
  gradeLevels: [],
  households: []
};
